import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { SelectionModel } from '@angular/cdk/collections';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatBadgeModule } from '@angular/material/badge';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { DataSourceOrdenacaoInsensivel } from '../../../../shared/helpers/ordenacao-tabela.helper';
import { CommonModule } from '@angular/common';
import { ConfirmModalComponent } from '../../../../shared/components/confirm-modal/confirm-modal.component';
import { EmptyListComponent } from '../../../../shared/components/empty-list/empty-list.component';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { LoadingBoxDefaultListComponent } from '../../../../shared/components/loading-box-default-list/loading-box-default-list.component';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { FormaEnvioService } from '../../../../shared/services/apis/vendas/forma-envio.service';
import { FormaEnvio } from '../../../../shared/services/models/vendas/forma-envio.model';
import { TipoLogistica } from '../../../../shared/services/responses/vendas/forma-envio-lookups.response';
import { UtilService } from '../../../../shared/services/util.service';

@Component({
  selector: 'app-formas-envio',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatCheckboxModule,
    MatMenuModule,
    MatSortModule,
    MatProgressSpinnerModule,
    MatBadgeModule,
    MatDialogModule,
    MatSelectModule,
    EmptyListComponent,
    HeaderComponent,
    LoadingBoxDefaultListComponent,
  ],
  templateUrl: './formas-envio.component.html',
  styleUrl: './formas-envio.component.css',
})
export class FormasEnvioComponent implements OnInit, OnDestroy {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Vendas;
  public model: FormGroup;
  public _paginaAtual: number = 0;
  public _tamanhoPagina: number = 20;
  public _totalRegistros: number = 0;
  public _dataSource = new DataSourceOrdenacaoInsensivel<FormaEnvio>();
  public _isLoading: boolean = false;
  public _tiposLogistica: TipoLogistica[] = [];
  public displayedColumns: string[] = ['select', 'descricao', 'tipo', 'status', 'actions'];
  public selection = new SelectionModel<FormaEnvio>(true, []);

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    this._dataSource.sort = sort;
  }

  constructor(
    private formBuilder: FormBuilder,
    private formaEnvioService: FormaEnvioService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private router: Router,
    private utilService: UtilService,
  ) {}

  ngOnInit(): void {
    this.model = this.formBuilder.group({
      descricao: [''],
      idTipoLogistica: [null],
      isAtivo: [null],
    });
    this.loadFilters();
    this.carregarLookups();
    this.carregarDados();
  }

  ngOnDestroy(): void {
    this.utilService.salvarFiltro(this.moduloEnum, this.funcionalidadeEnum, this.model, [
      '/configuracoes/vendas/formas-envio/cadastrar',
    ]);
  }

  private loadFilters(): void {
    const filtroSalvo = this.utilService.obterFiltro(this.moduloEnum, this.funcionalidadeEnum);
    if (filtroSalvo) this.model.patchValue(filtroSalvo);
  }

  private carregarLookups(): void {
    this.formaEnvioService.getLookups().subscribe({
      next: response => {
        if (!response.isError) this._tiposLogistica = response.tiposLogistica;
      },
    });
  }

  public carregarDados(reset: boolean = false): void {
    this._isLoading = true;
    const { descricao, idTipoLogistica, isAtivo } = this.model.value;
    this.formaEnvioService
      .getList(descricao || undefined, idTipoLogistica || undefined, isAtivo ?? undefined, this._paginaAtual, this._tamanhoPagina)
      .subscribe({
        next: response => {
          this._isLoading = false;
          if (response.isError) {
            this.alertService.show('Erro', response.errorDescription || 'Erro ao carregar formas de envio', AlertType.error);
            return;
          }
          this._totalRegistros = response.totalRegistros;
          if (reset) {
            this._dataSource.data = response.lista;
          } else {
            this._dataSource.data = [...this._dataSource.data, ...response.lista];
          }
        },
        error: err => {
          this._isLoading = false;
          this.alertService.show('Erro inesperado', err.message, AlertType.error);
        },
      });
  }

  public proximaPagina(): void {
    this._paginaAtual++;
    this.carregarDados();
  }

  public clickPesquisar(): void {
    this._paginaAtual = 0;
    this.selection.clear();
    this._dataSource.data = [];
    this.carregarDados(true);
  }

  public get qtdFiltrosAtivos(): number {
    let count = 0;
    const v = this.model.getRawValue();
    if (v.idTipoLogistica != null) count++;
    if (v.isAtivo != null) count++;
    return count;
  }

  public limparFiltros(): void {
    this.model.patchValue({ descricao: '', idTipoLogistica: null, isAtivo: null });
    this.clickPesquisar();
  }

  public isAllSelected(): boolean {
    return this.selection.selected.length === this._dataSource.data.length && this._dataSource.data.length > 0;
  }

  public toggleAllRows(): void {
    this.isAllSelected() ? this.selection.clear() : this.selection.select(...this._dataSource.data);
  }

  public editar(idFormaEnvio: number): void {
    this.router.navigate(['/configuracoes/vendas/formas-envio/cadastrar', idFormaEnvio]);
  }

  public toggleAtivo(item: FormaEnvio): void {
    this.formaEnvioService.toggleAtivo(item.idFormaEnvio, !item.isAtivo).subscribe({
      next: response => {
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription, AlertType.error);
        } else {
          item.isAtivo = !item.isAtivo;
          this.alertService.show('Sucesso', `Forma de envio ${item.isAtivo ? 'ativada' : 'inativada'} com sucesso.`, AlertType.success);
        }
      },
      error: err => this.alertService.show('Erro', err.message, AlertType.error),
    });
  }

  public abrirModalExcluir(item?: FormaEnvio): void {
    const isBulk = !item;
    const itemsToDelete = isBulk ? this.selection.selected : [item!];
    if (itemsToDelete.length === 0) return;

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Confirmar exclusão',
        description: 'As formas de envio selecionadas serão excluídas permanentemente. Confirma a exclusão?',
        buttonConfirm: 'Confirmar',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.confirm) {
        this._isLoading = true;
        const ids = itemsToDelete.map(i => i.idFormaEnvio);
        this.formaEnvioService.excluirBulk(ids).subscribe({
          next: r => {
            this._isLoading = false;
            if (r.isError) {
              this.alertService.show('Atenção', r.errorDescription, AlertType.warning);
            } else {
              this.alertService.show('Sucesso', 'Formas de envio excluídas com sucesso.', AlertType.success);
              this.selection.clear();
              this.clickPesquisar();
            }
          },
          error: err => {
            this._isLoading = false;
            this.alertService.show('Erro', err.message, AlertType.error);
          },
        });
      }
    });
  }
}
